import React, {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Alert, Button, EMPHASIS, ListCard, Tooltip} from '@forecast-it/design-system';
import {AGENTIC_WORKERS_MESSAGE, describeAcceptLabel} from './agenticWorkersMessages';
import {type AgentDeclaration, type AgentPage, type AgentResult} from './agentDeclaration';
import {getAgentsForPage} from './agentRegistry';
import {AGENT_DRAWER, describeFailureReason, openAgentDrawer, useAgentPanelState} from './agentRunState';
import {canSeeAgent} from './agentVisibility';
import {formatRunTime, useMinuteTick, type RunTimeMessages} from './agentRunTime';
import {agentControlDataCy} from './AgentToolbarButtons';
import {AgentRecordSignifiers, KEY_SEPARATOR} from './AgentRecordSignifiers';
import {AgentErrorBoundary} from './AgentErrorBoundary';

const PAGE_SIDE_MARGIN_PIXELS = 24;
const PANEL_GAP_PIXELS = 16;
const RUN_TIME_MESSAGES: RunTimeMessages = {
	justNow: AGENTIC_WORKERS_MESSAGE.PANEL_RUN_JUST_NOW,
	at: AGENTIC_WORKERS_MESSAGE.PANEL_RUN_AT,
};

const ROW_ACTION = {
	ACCEPT: 'ACCEPT',
	DELETE: 'DELETE',
	HIDE: 'HIDE',
} as const;
type RowAction = (typeof ROW_ACTION)[keyof typeof ROW_ACTION];

// The row whose accept, Delete or Hide was pressed. The pressed button leaves with the row.
type LeavingRow = {
	resultId: string;
	index: number;
	action: RowAction;
};

const PanelMargin = styled.div`
	margin: 0 ${PAGE_SIDE_MARGIN_PIXELS}px ${PANEL_GAP_PIXELS}px;
`;

const actionButtonKey = (resultId: string, action: RowAction): string => `${resultId}${KEY_SEPARATOR}${action}`;

const focusAgentControl = (agentId: string): void => {
	document.querySelector<HTMLElement>(`[data-cy="${agentControlDataCy(agentId)}"] button`)?.focus();
};

// One agent's panel. A component of its own because each agent holds its own run state.
const AgentPanelForAgent = ({declaration}: {declaration: AgentDeclaration}): JSX.Element | null => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {visibleResults, failedRun, acceptingResultIds, review, accept, deleteResult, hideResult, retry} =
		useAgentPanelState(declaration);
	const actionButtonsRef = useRef(new Map<string, HTMLButtonElement>());
	const retryButtonRef = useRef<HTMLButtonElement>(null);
	const leavingRowRef = useRef<LeavingRow | null>(null);
	const isRetryingRef = useRef(false);
	const isListShowing = visibleResults.length > 0;

	useMinuteTick(isListShowing);

	// A pressed button that leaves with its row or card hands focus on, instead of letting it fall back to the page.
	useEffect(() => {
		const leavingRow = leavingRowRef.current;
		if (leavingRow && !visibleResults.some(result => result.id === leavingRow.resultId)) {
			leavingRowRef.current = null;
			if (isListShowing) {
				const nextResult = visibleResults[Math.min(leavingRow.index, visibleResults.length - 1)];
				actionButtonsRef.current.get(actionButtonKey(nextResult.id, leavingRow.action))?.focus();
			} else if (failedRun) {
				retryButtonRef.current?.focus();
			} else {
				focusAgentControl(declaration.id);
			}
		} else if (leavingRow?.action === ROW_ACTION.ACCEPT && !acceptingResultIds.has(leavingRow.resultId)) {
			// The accept answered and its row stayed, so it did not apply. The row leaving later is not this press's doing.
			leavingRowRef.current = null;
			// A browser drops focus to the page while the loader disables the button, so it is handed back.
			if (document.activeElement === document.body) {
				actionButtonsRef.current.get(actionButtonKey(leavingRow.resultId, ROW_ACTION.ACCEPT))?.focus();
			}
		}
		if (isRetryingRef.current && failedRun === null) {
			isRetryingRef.current = false;
			focusAgentControl(declaration.id);
		}
	});

	const keepActionButton =
		(resultId: string, action: RowAction) =>
		(button: HTMLButtonElement | null): void => {
			const key = actionButtonKey(resultId, action);
			if (button) {
				actionButtonsRef.current.set(key, button);
			} else {
				actionButtonsRef.current.delete(key);
			}
		};

	const rowCommands: Record<RowAction, (result: AgentResult) => void> = {
		[ROW_ACTION.ACCEPT]: accept,
		[ROW_ACTION.DELETE]: deleteResult,
		[ROW_ACTION.HIDE]: hideResult,
	};

	const pressRowAction = (result: AgentResult, index: number, action: RowAction): void => {
		leavingRowRef.current = {
			resultId: result.id,
			index,
			action,
		};
		rowCommands[action](result);
	};

	const pressRetry = (): void => {
		isRetryingRef.current = true;
		retry();
	};

	const now = new Date();
	const failedTitle = formatMessage({id: AGENTIC_WORKERS_MESSAGE.FAILED_TITLE});
	const reviewLabel = formatMessage({id: AGENTIC_WORKERS_MESSAGE.RESULT_REVIEW});
	const deleteLabel = formatMessage({id: AGENTIC_WORKERS_MESSAGE.RESULT_DELETE});
	const hideTip = formatMessage({id: AGENTIC_WORKERS_MESSAGE.PANEL_HIDE});
	const laterLabel = formatMessage({id: AGENTIC_WORKERS_MESSAGE.PANEL_LATER});
	const dateLineOf = (result: AgentResult, agentDateLine: string): string => {
		const runTime = formatRunTime(result.producedAt, now, intl, RUN_TIME_MESSAGES);
		return runTime === null
			? agentDateLine
			: formatMessage(
					{id: AGENTIC_WORKERS_MESSAGE.PANEL_DATE_LINE},
					{
						runTime,
						dateLine: agentDateLine,
					}
			  );
	};

	// The live region stays mounted, empty and without height, while the agent is visible: screen readers often don't
	// announce a region that arrives already filled.
	return (
		<>
			<div role={'status'}>
				{failedRun && (
					<PanelMargin>
						<Alert
							type={Alert.TYPE.ERROR}
							text={`${failedTitle} ${describeFailureReason(declaration, failedRun, intl)}`}
						>
							<Button ref={retryButtonRef} emphasis={EMPHASIS.MEDIUM} onPress={pressRetry}>
								{formatMessage({id: AGENTIC_WORKERS_MESSAGE.FAILED_RETRY})}
							</Button>
							<Button
								emphasis={EMPHASIS.LOW}
								onPress={() => openAgentDrawer(declaration.id, AGENT_DRAWER.PAST_RUNS, failedRun.id)}
							>
								{formatMessage({id: AGENTIC_WORKERS_MESSAGE.FAILED_OPEN_RUN})}
							</Button>
						</Alert>
					</PanelMargin>
				)}
			</div>
			{isListShowing && (
				<PanelMargin>
					<ListCard
						aria-label={formatMessage(
							{id: AGENTIC_WORKERS_MESSAGE.PANEL_LABEL},
							{
								name: formatMessage(declaration.name),
								resultPlural: formatMessage(declaration.resultNoun.plural),
							}
						)}
						title={formatMessage(
							{id: AGENTIC_WORKERS_MESSAGE.PANEL_TITLE},
							{
								count: visibleResults.length,
								resultSingular: formatMessage(declaration.resultNoun.singular),
								resultPlural: formatMessage(declaration.resultNoun.plural),
							}
						)}
					>
						{visibleResults.map((result, index) => {
							const content = declaration.describeResult(result, intl);
							// The row's other actions wait with its accept, so a Delete never races it.
							const isAccepting = acceptingResultIds.has(result.id);
							return (
								<ListCard.Row
									key={result.id}
									leading={<AgentRecordSignifiers records={content.records} />}
									title={content.title}
									subtitle={dateLineOf(result, content.dateLine)}
									value={content.total}
									actions={
										<>
											<Button
												emphasis={EMPHASIS.LOW}
												disabled={isAccepting}
												onPress={() => review(result)}
											>
												{reviewLabel}
											</Button>
											<Button
												ref={keepActionButton(result.id, ROW_ACTION.ACCEPT)}
												emphasis={EMPHASIS.MEDIUM}
												waiting={isAccepting}
												onPress={() => pressRowAction(result, index, ROW_ACTION.ACCEPT)}
											>
												{describeAcceptLabel(declaration, result, intl)}
											</Button>
											<Tooltip content={hideTip}>
												<Button
													ref={keepActionButton(result.id, ROW_ACTION.HIDE)}
													emphasis={EMPHASIS.LOW}
													disabled={isAccepting}
													onPress={() => pressRowAction(result, index, ROW_ACTION.HIDE)}
												>
													{laterLabel}
												</Button>
											</Tooltip>
											<Tooltip content={deleteLabel}>
												<Button
													ref={keepActionButton(result.id, ROW_ACTION.DELETE)}
													emphasis={EMPHASIS.LOW}
													icon={'trash'}
													aria-label={deleteLabel}
													disabled={isAccepting}
													onPress={() => pressRowAction(result, index, ROW_ACTION.DELETE)}
												/>
											</Tooltip>
										</>
									}
								/>
							);
						})}
					</ListCard>
				</PanelMargin>
			)}
		</>
	);
};

type AgentPanelProps = {
	page: AgentPage;
};

export const AgentPanel = ({page}: AgentPanelProps): JSX.Element | null => {
	const visibleAgents = getAgentsForPage(page).filter(canSeeAgent);
	if (visibleAgents.length === 0) {
		return null;
	}
	return (
		<>
			{visibleAgents.map(declaration => (
				<AgentErrorBoundary key={declaration.id}>
					<AgentPanelForAgent declaration={declaration} />
				</AgentErrorBoundary>
			))}
		</>
	);
};
